/**
 * Derives a human-readable Risikoobjekt label from a RiskObject's
 * characteristics + name fallback.
 *  - KFZ → "<vehicleClass> <plate || VIN>"
 *  - Immobilie → "<street>, <plz> <city>"
 *  - Kennzahlen → "<year> — Umsatz <umsatz>"
 *  - other → falls back to the RiskObject's `name` field.
 */

export type RiskCharLite = {
  nameBezeichnung?: string;
  riskCharacteristicValue?: string;
};

export type RiskObjectLite = {
  riskType?: string;
  name?: string;
  characteristics?: RiskCharLite[];
};

const charValue = (
  chars: RiskCharLite[] | undefined,
  ...keys: string[]
): string => {
  if (!chars) return "";
  for (const k of keys) {
    const hit = chars.find((c) => c.nameBezeichnung === k);
    if (hit?.riskCharacteristicValue) return hit.riskCharacteristicValue;
  }
  return "";
};

const formatEuro = (raw: string): string => {
  if (!raw) return "";
  const num = Number(String(raw).replace(/\./g, "").replace(",", "."));
  if (!Number.isFinite(num)) return raw;
  return num.toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });
};

export const risikoobjektLabel = (r: RiskObjectLite): string => {
  const t = (r.riskType ?? "").toLowerCase();
  const chars = r.characteristics;

  if (t.includes("kfz")) {
    const cls = charValue(chars, "vehicleClass");
    const plate = charValue(chars, "licensePlate");
    const vin = charValue(chars, "vin");
    if (plate) return [cls, plate].filter(Boolean).join(" ");
    if (vin) return [cls, vin].filter(Boolean).join(" ");
    return cls || r.name || "KFZ";
  }

  if (t.includes("immo")) {
    const street = charValue(chars, "street", "Straße");
    const plz = charValue(chars, "postalCode", "PLZ");
    const city = charValue(chars, "city", "Ort");
    const parts = [street, [plz, city].filter(Boolean).join(" ")].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : r.name || "Immobilie";
  }

  if (t.includes("kennzahl")) {
    const year = String(new Date().getFullYear());
    const umsatzKey = `revenueTotal_${year}`;
    const umsatz = charValue(chars, umsatzKey);
    if (umsatz) return `${year} — Umsatz ${formatEuro(umsatz)}`;
    // Try without year suffix.
    const fallback = charValue(chars, "revenueTotal");
    if (fallback) return `${year} — Umsatz ${formatEuro(fallback)}`;
    return r.name || `Kennzahlen ${year}`;
  }

  return r.name || (r.riskType ?? "Risiko");
};

/**
 * Maps a riskType → Bereich number (1 Sachwerte / 2 Haftung / 3 Menschen / 4 Liquidität).
 * Falls back to 1 (Sachwerte) for unknown types.
 */
export const bereichForRiskType = (riskType: string | undefined): 1 | 2 | 3 | 4 => {
  const t = (riskType ?? "").toLowerCase();
  if (t.includes("kfz") || t.includes("immo") || t.includes("sachwert") || t.includes("transport")) return 1;
  if (t.includes("haftung") || t.includes("recht") || t.includes("cyber")) return 2;
  if (t.includes("mensch") || t.includes("personal") || t.includes("hr")) return 3;
  if (t.includes("kennzahl") || t.includes("liquid") || t.includes("ertrag") || t.includes("finanz"))
    return 4;
  return 1;
};
